import { Link, useNavigate } from "react-router-dom";
import React, { useState, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import { Panel, PanelBody, PanelHeader } from "../../components/panel/panel";
import FormInput from "../../components/CustomComponents/Input/formInput";
import APIs from "../../Constants/apis";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import CountryCitySelector from "../../components/CustomComponents/DropdownSelections/CountryCitySelector";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";

const initialSupplierData = {
  S_Name: "",
  S_Description: "",
  S_Type: "",
  S_Email: "",
  S_Contact: "",
  S_Address1: "",
  S_Address2: "",
  S_City: "",
  S_Country: "UK",
  Post_Code: "",
  S_Note: "",
  Is_Active: true,
  S_DateTime: new Date(),
  CompID: 1,
};

function AddSupplier() {
  const [selectedCity, setSelectedCity] = useState(null);
  const navigate = useNavigate();
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const [loading, setLoading] = useState(false);
  const [supplierData, setSupplierData] = useState(initialSupplierData);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  const handleInputChange = useCallback((e, isDropdown = false) => {
    if (isDropdown) {
      const { name, value } = e;
      setSupplierData((prev) => ({
        ...prev,
        [name]: value || "",
      }));
    } else {
      const { name, value, type, checked } = e.target;
      setSupplierData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  }, []);

  const handleToggleChange = useCallback((checked, name) => {
    setSupplierData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  }, []);

  const submitSupplier = async (submitData) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.POST_ADD_SUPPLIER, submitData);

      if (response.data.status) {
        showSuccessToast(response.data.message || "Supplier Successfully Created");
        navigate("/supplier/manageSupplier");
        setIsSubmitted(true);
      } else {
        showErrorToast(response.data.message || "Error Creating Supplier");
      }
    } catch (err) {
      if (err.response) {
        console.log(err);
        showErrorToast(err.response.data.message || "Error submitting supplier");
      } else if (err.request) {
        showErrorToast("No response received from server");
      } else {
        showErrorToast("Error in supplier submission");
      }
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!supplierData.S_Name.trim()) {
      showErrorToast("Please provide a valid supplier name");
      return;
    }

    if (!supplierData.S_Email || !validateEmail(supplierData.S_Email)) {
      showErrorToast("Please provide a valid email");
      return;
    }

    if (supplierData.S_Country && !selectedCity) {
      showErrorToast("Please select a city for the chosen country");
      return;
    }

    const submitData = {
      ...supplierData,
      S_DateTime: new Date(),
      S_City: selectedCity,
    };

    submitData.CompID = CompID;
    await submitSupplier(submitData);
  };

  useEffect(() => {
    if (isSubmitted) {
      const timer = setTimeout(() => {
        setIsSubmitted(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isSubmitted]);

  const RequiredLabel = ({ text }) => (
    <label className="form-label">
      {text} <span className="text-danger">*</span>
    </label>
  );

  const NormalLabel = ({ text }) => (
    <label className="form-label">{text}</label>
  );

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <Link to="/supplier">Manage Suppliers</Link>
            </li>
            <li className="breadcrumb-item active">Add Supplier</li>
          </ol>
          <h1 className="page-header mb-0">Add Supplier</h1>
        </div>
        <div className="ms-auto">
          <button
            className="btn px-4"
            onClick={handleSubmit}
            disabled={loading}
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: 'none',
              color: 'white',
              borderRadius: '20px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              boxShadow: `0 2px 8px ${themeColor}40`,
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
              }
            }}
          >
            {loading ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>

      <div className="card border-0">
        <Panel className="m-3">
          <PanelHeader>General</PanelHeader>
          <PanelBody>
            <h5 className="mb-4 text-primary fw-semibold">Supplier Information</h5>
            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <RequiredLabel text="Supplier Name" />
                <FormInput
                  name="S_Name"
                  placeholder="Enter Supplier Name"
                  type="text"
                  value={supplierData.S_Name}
                  onchange={handleInputChange}
                />
              </div>
              <div className="col-md-6">
                <NormalLabel text="Supplier Type" />
                <FormInput
                  name="S_Type"
                  placeholder="Enter Supplier Type"
                  type="text"
                  value={supplierData.S_Type}
                  onchange={handleInputChange}
                />
              </div>
              <div className="col-md-12">
                <NormalLabel text="Description" />
                <FormInput
                  name="S_Description"
                  placeholder="Enter Description"
                  type="text"
                  value={supplierData.S_Description}
                  onchange={handleInputChange}
                />
              </div>
            </div>

            <h5 className="mb-4 text-primary fw-semibold">Contact Information</h5>
            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <RequiredLabel text="Email" />
                <FormInput
                  name="S_Email"
                  placeholder="Enter Email"
                  type="email"
                  value={supplierData.S_Email}
                  onchange={handleInputChange}
                />
              </div>
              <div className="col-md-6">
                <NormalLabel text="Contact Number" />
                <FormInput
                  name="S_Contact"
                  placeholder="Enter Contact Number"
                  type="text"
                  value={supplierData.S_Contact}
                  onchange={handleInputChange}
                />
              </div>
            </div>

            <h5 className="mb-4 text-primary fw-semibold">Address Information</h5>
            <div className="row g-3 mb-4">
              <div className="col-md-12">
                <NormalLabel text="Address Line 1" />
                <FormInput
                  name="S_Address1"
                  placeholder="Enter Address Line 1"
                  type="text"
                  value={supplierData.S_Address1}
                  onchange={handleInputChange}
                />
              </div>
              <div className="col-md-12">
                <NormalLabel text="Address Line 2" />
                <FormInput
                  name="S_Address2"
                  placeholder="Enter Address Line 2"
                  type="text"
                  value={supplierData.S_Address2}
                  onchange={handleInputChange}
                />
              </div>
            </div>

            <div className="row g-3 mb-4">
              <RequiredLabel text="Country / City" />
              <CountryCitySelector
                selectedCountry={supplierData.S_Country}
                selectedCity={selectedCity}
                setSelectedCity={setSelectedCity}
                onChange={(updatedData) => setSupplierData((prev) => ({ ...prev, ...updatedData }))}
                setLoading={setLoading}
                isDarkMode={isDarkMode}
                countryFieldName="S_Country"
                cityFieldName="S_City"
                selectedValueIsLabel={true}
              />
            </div>

            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <NormalLabel text="Post Code" />
                <FormInput
                  name="Post_Code"
                  placeholder="Enter Post Code"
                  type="text"
                  value={supplierData.Post_Code}
                  onchange={handleInputChange}
                />
              </div>
            </div>

            <h5 className="mb-4 text-primary fw-semibold">Additional Information</h5>
            <div className="row g-3 mb-4">
              <div className="col-md-12">
                <NormalLabel text="Notes" />
                <FormInput
                  name="S_Note"
                  placeholder="Enter Notes"
                  type="text"
                  value={supplierData.S_Note}
                  onchange={handleInputChange}
                />
              </div>
            </div>

            <h5 className="mb-4 text-primary fw-semibold">Status</h5>
            <div className="row g-3 mb-4">
              <div className="col-md-3">
                <ToggleSwitch
                  isChecked={supplierData.Is_Active}
                  onToggle={(checked) => handleToggleChange(checked, "Is_Active")}
                  name={"Is_Active"}
                  label={"Active"}
                  disabled={false}
                  themeColor={themeColor}
                />
              </div>
            </div>

          </PanelBody>
        </Panel>
      </div>
    </div>
  );
}

export default AddSupplier;